/**
 * Condition sets and scripts, as the server stores them (D43;
 * core/content/script.py). A condition set says what the world is: sources on
 * external ports with named settings, fluids, and initial fill. A script says
 * what to do: steps of actions and operations, how each finishes, and checks (D45).
 */

export type SourceKind = "pressure" | "vacuum" | "flow" | "open" | "sealed";
export const SOURCE_KINDS: readonly SourceKind[] = ["pressure", "vacuum", "flow", "open", "sealed"];

export interface SourceSetting {
  name: string;
  kind: SourceKind;
  /** Pascal gauge for pressure and vacuum (vacuum negative); m³/s for flow. */
  magnitude?: number | null;
}

export interface Source {
  name: string;
  /** An external port, `node.port`. */
  port: string;
  settings: SourceSetting[];
  initial: string;
  description?: string;
}

export interface Fluid {
  name: string;
  description?: string;
  phase?: "liquid" | "gas";
  density?: number | null;
  viscosity?: number | null;
  /** How the preview draws it, as #rrggbb; unset, it takes the next of the preview's colours. */
  colour?: string | null;
}

export interface Fill {
  /** A component or line. */
  target: string;
  fluid: string;
  /** m³: how much a bag holds at the start. */
  volume?: number | null;
  /** m³: the gas a bag holds above its liquid at the start, which follows the liquid out when it is run dry. */
  headspace?: number | null;
}

/** Whether a bag or port is where fluid comes in or goes out, in this setup. */
export interface Designation {
  target: string;
  role: "input" | "output";
}

export interface ConditionSetDocument {
  sources?: Source[];
  fluids?: Fluid[];
  initial_fill?: Fill[];
  designations?: Designation[];
  notes?: string;
}

export interface SetState {
  kind: "set_state";
  target: string;
  state: string;
}

/** Where a regulator's setpoint starts (pascal gauge): the script's, as a valve's starting state is. */
export interface StartSetpoint {
  target: string;
  value: number;
}

export interface SetSource {
  kind: "set_source";
  source: string;
  setting: string;
}

/** One linear piece of a ramp: from a rate to a rate over a time (m³/s, s). Equal rates hold. */
export interface RampSegment {
  from_rate: number;
  to_rate: number;
  seconds: number;
}

export type PumpMode = "off" | "run" | "volume" | "time" | "ramp";

/**
 * What a pump does in a step (D45): off; run at a rate; a rate and a volume
 * (a dispense); a rate and a time; or ramp segments. It stops when the step
 * ends unless `keep_running`.
 */
export interface RunPump {
  kind: "pump";
  target: string;
  mode: PumpMode;
  /** m³/s. */
  rate?: number | null;
  /** m³. */
  volume?: number | null;
  seconds?: number | null;
  segments?: RampSegment[];
  keep_running?: boolean;
}

/** A mixer's speed (Hz: revolutions per second), a thermal zone's temperature (K), or a regulator's outlet pressure (Pa gauge, D65). */
export interface SetSetpoint {
  kind: "setpoint";
  target: string;
  quantity: "temperature" | "speed" | "pressure";
  value: number;
}

/** One linear piece of a source's ramp: from a value to a value over a time. Equal values hold. */
export interface ProfileSegment {
  from_value: number;
  to_value: number;
  seconds: number;
}

export type ProfileQuantity = "pressure" | "vacuum" | "flow";

/**
 * A source driven through a profile in a step, as the instrument runs it
 * (D52). `stairs`: from `start` to `end` by `increment`, each level held
 * `hold` seconds, the last level `end`; `ramp`: `segments`. Pascal gauge for
 * pressure and vacuum (vacuum negative), m³/s for flow. A finish condition met
 * first stops it where it is; after the step the source stays where it got to.
 */
export interface RunSource {
  kind: "source_profile";
  source: string;
  quantity: ProfileQuantity;
  mode: "stairs" | "ramp";
  start?: number | null;
  end?: number | null;
  increment?: number | null;
  hold?: number | null;
  segments?: ProfileSegment[];
}

/** The shape a profile has, whatever it drives: a source (D52) or a regulator (D65). */
export type Profile = Pick<RunSource, "quantity" | "mode" | "start" | "end" | "increment" | "hold" | "segments">;

/**
 * A regulator's outlet driven through a profile in a step (D65): a pressure
 * sweep, as a source's profile is, but of a part the design holds. Pascal
 * gauge. In a step a regulator has one setpoint, held or profiled.
 */
export interface RunSetpoint extends Profile {
  kind: "setpoint_profile";
  target: string;
  quantity: "pressure";
}

export type Action = SetState | SetSource | RunPump | RunSource | SetSetpoint | RunSetpoint;

export type Comparison = "lt" | "le" | "gt" | "ge";
export const COMPARISONS: readonly { value: Comparison; label: string }[] = [
  { value: "lt", label: "below" },
  { value: "le", label: "at or below" },
  { value: "gt", label: "above" },
  { value: "ge", label: "at or above" },
];

/**
 * When a sensor or pressure condition counts as met (D58). `edge`: only once
 * seen unmet, then met, a crossing in the comparison's direction; else a level,
 * met already at the start counting. `hold`: met, then held that long (s).
 * `ignore`: a reading, met or unmet, lasting less than this does not count (s).
 */
export interface Trigger {
  edge?: boolean;
  hold?: number | null;
  ignore?: number | null;
}

/** One way a step can finish (D5's closed vocabulary). */
export type Condition =
  | ({
      kind: "sensed";
      sensor: string;
      /** The reading; its change since the step began; or its drop below its highest reading in the step (D52). */
      measure?: "reading" | "change" | "drop";
      comparison: Comparison;
      value: number;
    } & Trigger)
  | ({ kind: "pressure_threshold"; at: string; comparison: Comparison; pressure: number } & Trigger)
  | { kind: "volume_delivered"; through: string; volume: number }
  | { kind: "operator_wait"; instruction: string }
  /** Until a vessel runs empty (D62): a timed transfer's intent, where nothing senses it. */
  | { kind: "vessel_empty"; vessel: string };

export type ConditionKind = Condition["kind"];
export const CONDITION_KINDS: readonly { value: ConditionKind; label: string }[] = [
  { value: "sensed", label: "A sensor reads" },
  { value: "pressure_threshold", label: "A pressure" },
  { value: "volume_delivered", label: "A volume delivered" },
  { value: "operator_wait", label: "The operator continues" },
  { value: "vessel_empty", label: "A vessel runs empty" },
];

/** How a step ends: whichever comes first (D45). */
export interface Finish {
  /** When every operation that completes (dispense, timed run, ramp) has. */
  complete?: boolean;
  conditions?: Condition[];
  /** Seconds. */
  max_seconds?: number | null;
  /** At the maximum time: end normally, or fail (R7.4). */
  on_max?: "continue" | "fail";
}

/** Passes or fails as the step ends: a sensor's reading or change, or a record of the step against a spec (D54). */
export interface Check {
  local_id: string;
  sensor?: string | null;
  record?: string | null;
  measure?: "reading" | "change";
  comparison: Comparison;
  /** The spec: a value, or a component's parameter, `node.key`, as it stands (D55). */
  value?: number | null;
  parameter?: string | null;
  description?: string;
}

/**
 * A value the step reports, with no pass or fail (D53): a sensor's reading or
 * a source's value (a profile's level where it stopped), as the step ends or
 * when the condition that finished it was first met, before any hold.
 */
export interface StepRecord {
  local_id: string;
  sensor?: string | null;
  source?: string | null;
  when?: "end" | "met";
  /** Its value at that moment, or up to it its highest, its lowest, or its peak: furthest from ambient (D56, D57). */
  measure?: "value" | "max" | "min" | "peak";
  description?: string;
}

export interface Step {
  local_id: string;
  label: string;
  actions?: Action[];
  finish: Finish;
  checks?: Check[];
  records?: StepRecord[];
  notes?: string;
}

export interface ScriptDocument {
  /** The condition set behind step zero, by entity id (D44). */
  conditions?: string | null;
  /** The script whose end this one starts from, by entity id (D44). */
  starts_after?: string | null;
  /** Step zero: where components start. Unlisted, their declared initial state. */
  initial_states?: SetState[];
  /** Step zero's source settings, over the condition set's or the parent's end (D44). */
  initial_sources?: SetSource[];
  /** Step zero's regulator setpoints (pascal gauge, D65), over the parent's end. Unlisted: none, the outlet relieved. */
  initial_setpoints?: StartSetpoint[];
  steps?: Step[];
  description?: string;
}

/** A fluid name every condition set has. */
export const AIR = "air";
